
import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import emlIcon from '../../assets/img/eml.png';
import {
  AiValidationHistoryRow,
  BlurFlaggingData,
  DEFAULT_IMAGE_INFO,
  PreCheckResult,
  extractPreCheckResults,
  formatRuntimeStatus,
  getStatusSx,
  normalizeStatusKey,
} from './aiValidationHistoryTypes';

interface AiValidationHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  selectedRow: AiValidationHistoryRow | null;
}

const SECTION_HEADER_SX = {
  fontFamily: 'Montserrat',
  fontWeight: 600,
  fontSize: '11px',
  color: '#9CA3AF',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  mb: 1,
};

const formatAssetType = (assetType: string): React.ReactNode => {
  const normalized = assetType.toLowerCase().trim();

  if (normalized.includes('image') || normalized === 'img') return '🖼 Image';
  if (normalized.includes('eml') || normalized.includes('email')) {
    return (
      <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <Box component='img' src={emlIcon} alt='EML' sx={{ width: 12, height: 12, mr: 0.6, objectFit: 'contain' }} />
        EML
      </Box>
    );
  }
  if (normalized.includes('audio')) return '🎵 Audio';
  if (normalized.includes('video')) return '🎬 Video';
  if (normalized.includes('text')) return 'Image Text';
  if (normalized.includes('document') || normalized.includes('doc')) return '📄 Document';

  return assetType || '-';
};

const formatRuntimeDate = (dateValue: string): string => {
  if (!dateValue || dateValue === '-') {
    return '-';
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return dateValue;
  }

  return parsedDate
    .toLocaleString('en-US', { hour12: true })
    .replace('AM', 'am')
    .replace('PM', 'pm');
};

const formatKilobytes = (bytes: number | null): string => {
  if (bytes === null) return '-';
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const getPreCheckAccent = (status: PreCheckResult['status']) => {
  if (status === 'pass') {
    return {
      icon: '✓',
      backgroundColor: '#ECFDF3',
      borderColor: '#BBF7D0',
      textColor: '#166534',
    };
  }

  if (status === 'warning') {
    return {
      icon: '⚠',
      backgroundColor: '#FEFCE8',
      borderColor: '#FDE68A',
      textColor: '#854D0E',
    };
  }

  return {
    icon: '✕',
    backgroundColor: '#FEF2F2',
    borderColor: '#FECACA',
    textColor: '#991B1B',
  };
};

const AiValidationHistoryDrawer: React.FC<AiValidationHistoryDrawerProps> = ({
  open,
  onClose,
  loading,
  selectedRow,
}) => {
  return (
    <Drawer anchor='right' open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 620 }, maxWidth: '100vw', p: 3, height: '100%', boxSizing: 'border-box', overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant='h6' fontWeight='bold' sx={{ fontFamily: 'Montserrat' }}>
            Validation
          </Typography>
          <IconButton onClick={onClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : selectedRow && (
          <DrawerContent selectedRow={selectedRow} />
        )}
      </Box>
    </Drawer>
  );
};

interface DrawerContentProps {
  selectedRow: AiValidationHistoryRow;
}

const DrawerContent: React.FC<DrawerContentProps> = ({ selectedRow }) => {
  const imageInfo = selectedRow.imageInfo ?? DEFAULT_IMAGE_INFO;

  const runtimeCards: { label: string; value: React.ReactNode; isStatus?: boolean }[] = [
    { label: 'Name', value: selectedRow.name },
    { label: 'Asset Type', value: formatAssetType(selectedRow.assetType) },
    { label: 'Status', value: formatRuntimeStatus(selectedRow.status), isStatus: true },
    { label: 'Latency', value: selectedRow.latency !== '-' ? `${selectedRow.latency}ms` : '-' },
    { label: 'Prompt Tokens', value: selectedRow.promptTokens.toLocaleString('en-US') },
    { label: 'Completion Tokens', value: selectedRow.completionTokens.toLocaleString('en-US') },
    { label: 'Total Tokens', value: selectedRow.totalTokens.toLocaleString('en-US') },
    { label: 'Est. Cost', value: `$${selectedRow.cost.toFixed(6)}` },
    { label: 'Date', value: formatRuntimeDate(selectedRow.date) },
  ];

  const imagePropertyCards: { label: string; value: string }[] = [
    {
      label: 'Dimensions',
      value:
        imageInfo.width !== null && imageInfo.height !== null
          ? `${imageInfo.width} x ${imageInfo.height}px`
          : '-',
    },
    {
      label: 'Megapixels',
      value: imageInfo.megapixels !== null ? `${imageInfo.megapixels.toFixed(1)} MP` : '-',
    },
    {
      label: 'Aspect Ratio',
      value: imageInfo.aspectRatio !== null ? imageInfo.aspectRatio.toFixed(3) : '-',
    },
    { label: 'MIME Type', value: imageInfo.mimeType || '-' },
    { label: 'File Size', value: formatKilobytes(imageInfo.fileSize) },
    { label: 'Colour Depth', value: imageInfo.bits !== null ? `${imageInfo.bits}-bit` : '-' },
    {
      label: 'True Colour',
      value: imageInfo.isTrueColor === null ? '-' : imageInfo.isTrueColor ? 'Yes' : 'No',
    },
  ];

  const preCheckResults =
    selectedRow.preCheckResults.length > 0
      ? selectedRow.preCheckResults
      : extractPreCheckResults(selectedRow.preChecks);

  const dedupedPreCheckResults = preCheckResults.filter((check) => {
    const titleLower = check.title.toLowerCase();
    if (selectedRow.piiData?.enabled && titleLower.includes('pii')) return false;
    if (selectedRow.duplicateCheckData && titleLower.includes('duplicate')) return false;
    return true;
  });

  type ResultRowItem = { key: string; status: 'pass' | 'fail'; title: string; detail: string };

  const piiRows: ResultRowItem[] = [];
  if (selectedRow.piiData?.enabled) {
    const piiItem = selectedRow.piiData;
    const piiDetail = piiItem.isPiiDetected
        ? piiItem.detectedTypes.length > 0
          ? piiItem.detectedTypes.join(', ')
          : 'PII detected'
        : 'Nothing detected';
    piiRows.push({
      key: 'pii-flagging',
      status: piiItem.isPiiDetected ? 'fail' : 'pass',
      title: 'PII Flagging',
      detail: piiDetail,
    });
  }

  const dupCheckRows: ResultRowItem[] = [];
  if (selectedRow.duplicateCheckData) {
    dupCheckRows.push({
      key: 'duplicate-check',
      status: selectedRow.duplicateCheckData.isDuplicate ? 'fail' : 'pass',
      title: 'Duplicate Check',
      detail: selectedRow.duplicateCheckData.isDuplicate ? 'Duplicate detected' : 'No duplicate found',
    });
  }

  const blurFlaggingRows: ResultRowItem[] = [];
  if (selectedRow.blurFlaggingData?.enabled) {
    const blurItem = selectedRow.blurFlaggingData;
    blurFlaggingRows.push({
      key: 'blur-flagging',
      status: blurItem.passed ? 'pass' : 'fail',
      title: 'Blur Check',
      detail: blurItem.passed ? 'Not blurry' : (blurItem.details !== '-' ? blurItem.details : 'Blur detected'),
    });
  }

  const extraResultRows = [...piiRows, ...dupCheckRows, ...blurFlaggingRows];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Validation Runtime */}
      <Box>
        <Typography sx={SECTION_HEADER_SX}>Run Details</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
          {runtimeCards.map(({ label, value, isStatus }) => (
            <Card key={label} sx={{ borderRadius: 2, boxShadow: 0, border: '1.5px solid #CBD5E1', backgroundColor: '#F9FAFB' }}>
              <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Typography sx={{ fontFamily: 'Montserrat', fontSize: '11px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>
                  {label}
                </Typography>
                {isStatus ? (
                  <Box
                    component='span'
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 1.25,
                      py: 0.55,
                      borderRadius: 999,
                      fontFamily: 'Montserrat',
                      fontSize: '11px',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      ...getStatusSx(normalizeStatusKey(selectedRow.status)),
                    }}
                  >
                    {value}
                  </Box>
                ) : (
                  <Typography sx={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 700, color: '#111827', wordBreak: 'break-word' }}>
                    {value}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      {/* Image Properties */}
      <Box>
        <Typography sx={SECTION_HEADER_SX}>Image Properties</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.25 }}>
          {imagePropertyCards.map(({ label, value }) => (
            <Card key={label} sx={{ borderRadius: 2, boxShadow: 0, border: '1.5px solid #CBD5E1', backgroundColor: '#F9FAFB' }}>
              <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Typography sx={{ fontFamily: 'Montserrat', fontSize: '11px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>
                  {label}
                </Typography>
                <Typography sx={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '13px', color: '#111827' }}>
                  {value}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      {/* Pre-check Results */}
      <Box>
        <Typography sx={SECTION_HEADER_SX}>Results</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {dedupedPreCheckResults.map((preCheck, index) => {
            const accent = getPreCheckAccent(preCheck.status);

            return (
              <Box
                key={`${preCheck.title}-${index}`}
                sx={{
                  backgroundColor: accent.backgroundColor,
                  border: `1.5px solid ${accent.borderColor}`,
                  borderRadius: 1.5,
                  px: 1.25,
                  py: 1,
                }}
              >
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'start', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box
                      component='span'
                      sx={{
                        minWidth: 20,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: 'Montserrat',
                        fontWeight: 900,
                        fontSize: '15px',
                        lineHeight: 1,
                        color: accent.textColor,
                        mt: 0.1,
                      }}
                    >
                      {accent.icon}
                    </Box>
                    <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 700, color: '#111827' }}>
                      {preCheck.title}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', color: '#6B7280', textAlign: 'right' }}>
                    {preCheck.description}
                  </Typography>
                </Box>
              </Box>
            );
          })}

          {extraResultRows.map(({ key, status, title, detail }) => {
            const accent = getPreCheckAccent(status);
            return (
              <Box
                key={key}
                sx={{
                  backgroundColor: accent.backgroundColor,
                  border: `1.5px solid ${accent.borderColor}`,
                  borderRadius: 1.5,
                  px: 1.25,
                  py: 1,
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box
                      component='span'
                      sx={{
                        minWidth: 20,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: 'Montserrat',
                        fontWeight: 900,
                        fontSize: '15px',
                        lineHeight: 1,
                        color: accent.textColor,
                        mt: 0.1,
                      }}
                    >
                      {accent.icon}
                    </Box>
                    <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 700, color: '#111827', mb: 0.2 }}>
                      {title}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', color: '#6B7280' }}>
                    {detail}
                  </Typography>
                </Box>
              </Box>
            );          })}
        </Box>
      </Box>

      {/* Validation Notes */}
      <Box>
        <Typography sx={SECTION_HEADER_SX}>Validation</Typography>
        {(() => {
          const vs = selectedRow.validationSummary;
          if (!vs) {
            return (
              <Card sx={{ borderRadius: 2, boxShadow: 0, border: '1.5px solid #FDE68A', backgroundColor: '#FEFCE8' }}>
                <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                  <Typography sx={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 700, color: '#111827' }}>
                    -
                  </Typography>
                </CardContent>
              </Card>
            );
          }

          const isPass = normalizeStatusKey(vs.finalStatus) === 'pass';
          const cardBorderColor = isPass ? '#BBF7D0' : '#FECACA';
          const cardBgColor = isPass ? '#ECFDF3' : '#FEF2F2';
          const statusAccent = isPass
            ? { color: '#166534', chipBg: '#DCFCE7', chipBorder: '#86EFAC' }
            : { color: '#991B1B', chipBg: '#FEE2E2', chipBorder: '#FCA5A5' };

          return (
            <Card sx={{ borderRadius: 2, boxShadow: 0, border: `1.5px solid ${cardBorderColor}`, backgroundColor: cardBgColor }}>
              <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography sx={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 700, color: '#111827', flex: 1 }}>
                    {vs.reasoning}
                  </Typography>
                  <Box
                    component='span'
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 1,
                      py: 0.35,
                      borderRadius: 999,
                      border: `1px solid ${statusAccent.chipBorder}`,
                      backgroundColor: statusAccent.chipBg,
                      fontFamily: 'Montserrat',
                      fontSize: '10px',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      color: statusAccent.color,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {vs.finalStatus.toUpperCase()}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })()}
      </Box>

      {/* AI Validation Response */}
      <Box>
        <Typography sx={SECTION_HEADER_SX}>Validation Response</Typography>
        <Box sx={{ border: '1.5px solid #CBD5E1', borderRadius: 2, overflow: 'hidden' }}>
          <AiResponseHeader responseText={selectedRow.aiValidationResponse} />
          <TextField
            fullWidth
            multiline
            rows={10}
            value={selectedRow.aiValidationResponse}
            InputProps={{ readOnly: true }}
            sx={{
              '& .MuiInputBase-root': {
                fontFamily: 'Montserrat',
                fontSize: '13px',
                backgroundColor: '#F9FAFB',
                borderRadius: 0,
              },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const AiResponseHeader: React.FC<{ responseText: string }> = ({ responseText }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(responseText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1.5, py: 0.75, backgroundColor: '#F3F4F6', borderBottom: '1px solid #E5E7EB' }}>
      <Typography sx={{ fontFamily: 'Montserrat', fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        AI Response
      </Typography>
      <Button
        size='small'
        onClick={handleCopy}
        sx={{ fontFamily: 'Montserrat', fontSize: '11px', fontWeight: 600, color: copied ? '#16A34A' : '#6B7280', textTransform: 'none', minWidth: 0, p: 0, '&:hover': { backgroundColor: 'transparent', color: '#111827' } }}
        disableRipple
      >
        {copied ? 'Copied!' : 'Copy'}
      </Button>
    </Box>
  );
};

export default AiValidationHistoryDrawer;
